import React, { memo, useCallback, use, useEffect, useMemo, useState } from "react";
import type { BaseSelectRef } from "rc-select";
import { blue } from "@ant-design/colors";
import { Link } from "react-router-dom";
import { DefaultOptionType } from "rc-select/lib/Select";
import { Flex, Select, Tooltip } from "antd";

import { Contacts, GQL } from "@binale-tech/shared";
import { PlusOutlined } from "@ant-design/icons";
import { ContactsContext } from "scripts/context/ContactsContext";

import styles from "./contactSelect.module.scss";
import { useCanWrite } from "../../../../../../scripts/infrastructure/hooks";
import { RefCallBack } from "react-hook-form";

type TProps = {
    placeholder?: React.ReactNode;
    tooltipPlacement?: React.ComponentProps<typeof Tooltip>["placement"];
    onChange?: (v: string) => void;
    value?: string;
    disabled?: boolean;
};

export const ContactSelect = memo(
    ({
        ref,
        placeholder,
        onChange,
        value,
        tooltipPlacement = "top",
        disabled,
        ...restProps
    }: TProps & {
        ref?: React.RefObject<BaseSelectRef> | RefCallBack;
    }) => {
        const { contacts, contactsMap } = use(ContactsContext);
        const [placeholderText, setPlaceholderText] = useState(placeholder);
        const [searchValue, setSearchValue] = useState("");

        const hasContactsWriteAccess = useCanWrite(GQL.IProductKey.Contacts);

        const suggestCreateContext = Boolean(!disabled && hasContactsWriteAccess && !value && searchValue.length);

        const options: DefaultOptionType[] = useMemo(() => {
            return (contacts || []).map((item: Contacts.Contact) => ({
                label: Contacts.getLabelName(item) || item.internalName,
                value: item.uuid,
            }));
        }, [contacts]);

        const handleChange = useCallback(
            (changedValue: string, option: DefaultOptionType | DefaultOptionType[]) => {
                onChange?.(changedValue ?? undefined);
            },
            [onChange]
        );

        const filterOption = useCallback(
            (inputValue: string, option: DefaultOptionType) => {
                if (!option) {
                    return false;
                }
                const item = contactsMap.get(String(option.value));

                if (!item) {
                    return false;
                }

                const internalName = item.internalName.toLowerCase();
                const label = Contacts.getLabelName(item).toLowerCase();
                const term = inputValue.toLowerCase();

                return internalName.includes(term) || label.includes(term);
            },
            [contactsMap]
        );

        useEffect(() => {
            if (placeholder === "Diverse" && !disabled && onChange) {
                onChange(undefined);
                setPlaceholderText(undefined);
            }
        }, [disabled, placeholder]);

        return (
            <Select<string>
                ref={ref}
                {...restProps}
                disabled={disabled}
                placeholder={placeholderText}
                options={options}
                value={value}
                onChange={handleChange}
                onSearch={setSearchValue}
                filterOption={filterOption}
                allowClear={!suggestCreateContext}
                showSearch
                optionRender={option => (
                    <Flex vertical>
                        {contactsMap && (
                            <small style={{ color: "#888" }}>
                                {contactsMap.get(option.value as string)?.internalName}
                            </small>
                        )}
                        <div>{option.label || <>&nbsp;</>}</div>
                    </Flex>
                )}
                suffixIcon={
                    suggestCreateContext ? (
                        <Tooltip
                            title="Add Contact with this name in the Contacts module"
                            color={blue[5]}
                            placement={tooltipPlacement}
                        >
                            <Link target={"_blank"} to={`/contacts?modal=${searchValue}`} className={styles.addBtn}>
                                <PlusOutlined />
                            </Link>
                        </Tooltip>
                    ) : null
                }
            />
        );
    }
);
